import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../core/store/store';
import { updateUser } from '../../core/store/slices/userSlice';
import { getApiBaseUrl } from '../../core/config/api';

/**
 * Hook pour synchroniser périodiquement l'argent et les médailles de l'utilisateur
 * @param interval Intervalle de mise à jour en millisecondes (défaut: 5000ms = 5 secondes)
 * @param enabled Activer ou désactiver la synchronisation (défaut: true)
 */
export const useUserProfileSync = (interval: number = 5000, enabled: boolean = true) => {
  const dispatch = useDispatch();
  const { currentUser } = useSelector((state: RootState) => state.user);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!enabled || !currentUser?.id) {
      return;
    }

    const fetchUserProfile = async () => {
      try {
        const token =
          localStorage.getItem('token') ||
          localStorage.getItem('auth_token') ||
          localStorage.getItem('authToken');
        if (!token) {
          return;
        }

        const response = await fetch(`${getApiBaseUrl(true)}/auth/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            const rawArgents = Number(data.user.argents);
            const rawMedailles = Number(
              data.user.medailles ??
                data.user.medaille ??
                data.user.medailles_total ??
                data.user.medals ??
                data.user.medals_total
            );
            const patch: { argents?: number; medailles?: number; batiments?: number; vehicules?: number; statistiques?: import('../types/user').UserStats } = {};
            if (Number.isFinite(rawArgents)) patch.argents = rawArgents;
            if (Number.isFinite(rawMedailles)) patch.medailles = rawMedailles;
            if (data.user.batiments != null) patch.batiments = Number(data.user.batiments);
            if (data.user.vehicules != null) patch.vehicules = Number(data.user.vehicules);
            if (data.user.statistiques?.personnel != null) patch.statistiques = {
              personnel: Number(data.user.statistiques.personnel),
              batiments: Number(data.user.statistiques.batiments ?? data.user.batiments ?? 0),
              vehicules: Number(data.user.statistiques.vehicules ?? data.user.vehicules ?? 0),
              interventions: Number(data.user.statistiques.interventions ?? 0),
              tickets_support: Number(data.user.statistiques.tickets_support ?? 0),
              amis: Number(data.user.statistiques.amis ?? 0),
            };
            // Ne met à jour que les champs réellement fournis/valides.
            if (Object.keys(patch).length > 0) {
              dispatch(updateUser(patch));
            }
          }
        }
      } catch (error) {
        console.warn('Erreur lors de la synchronisation du profil:', error);
      }
    };

    // Exécuter immédiatement une première fois
    fetchUserProfile();

    // Puis exécuter périodiquement
    intervalRef.current = setInterval(fetchUserProfile, interval);

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [dispatch, currentUser?.id, interval, enabled]);
};

export default useUserProfileSync;

